/**
 * Закупы. Главная мысль экрана: торговаться надо за ОСТАВШЕГОСЯ подписчика,
 * а не за пришедшего. Поэтому цена удержанного — единственное крупное число
 * в карточке, а способ подсчёта и его уверенность подписаны рядом.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Purchase } from "../lib/api";
import { useSession } from "../session";
import {
  Badge,
  Card,
  ConfidenceBadge,
  Empty,
  ErrorBox,
  LoadingDashboard,
  MetricCard,
  Notice,
  PageHeader,
  Paywall,
  Why,
} from "../components/ui";
import { IconMegaphone, IconPlus } from "../components/icons";
import { DASH, date, decimal, money, num, pct, plural } from "../lib/format";

interface Methodology {
  window_hours: number;
  baseline_days: number;
  has_admin_rights: boolean;
  methods: { key: string; title: string; accuracy: string; requires: string; note: string }[];
  retention: string;
  engagement: string;
}

export default function AdsPage() {
  const { channelId, can } = useSession();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const purchases = useQuery({
    queryKey: ["purchases", channelId],
    queryFn: () => api.get<{ purchases: Purchase[] }>(`/ads/${channelId}/purchases`),
    enabled: Boolean(channelId),
  });
  const methodology = useQuery({
    queryKey: ["ads-methodology", channelId],
    queryFn: () => api.get<Methodology>(`/ads/${channelId}/methodology`),
    enabled: Boolean(channelId),
  });

  if (!channelId) {
    return (
      <Card>
        <Empty title="Сначала подключите канал" actions={<Link to="/channels"><button className="primary">Каналы</button></Link>} />
      </Card>
    );
  }
  if (purchases.error instanceof ApiError && purchases.error.isPaywall) {
    return <Paywall what="Разбор рекламных закупок" />;
  }
  if (purchases.isLoading) return <LoadingDashboard />;
  if (purchases.error) return <ErrorBox error={purchases.error} />;
  const rows = purchases.data!.purchases;
  const summary = summarize(rows);

  return (
    <>
      <PageHeader
        eyebrow="Детальная аналитика"
        title="Закупы"
        lede="Реклама, которую канал ПОКУПАЕТ, чтобы привести подписчиков. Не путать с рекламной нагрузкой в «Здоровье» — там чужие размещения в вашем канале."
        actions={
          can("write_ads") && (
            <button className="primary" onClick={() => setAdding((value) => !value)}>
              {!adding && <IconPlus size={15} />}
              {adding ? "Отмена" : "Добавить размещение"}
            </button>
          )
        }
      />

      {methodology.data && !methodology.data.has_admin_rights && (
        <Notice tone="warn">
          У нашего аккаунта нет прав администратора в канале. Точная атрибуция по
          ссылкам-приглашениям и число удержанных подписчиков без них недоступны — вместо них
          оценка и прочерки. Выдайте права на площадке и отметьте это в{" "}
          <Link to="/settings">настройках</Link>.
        </Notice>
      )}

      {adding && (
        <PurchaseForm
          channelId={channelId}
          onDone={() => {
            setAdding(false);
            queryClient.invalidateQueries({ queryKey: ["purchases", channelId] });
          }}
        />
      )}

      {/* Итог по всем размещениям считается из тех же чисел, что и карточки:
          ни одного показателя «для красоты» здесь нет. Смешанные валюты не
          складываются — вместо суммы стоит прочерк. */}
      {rows.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "var(--s3)" }}>
          <MetricCard
            small
            name="Размещений"
            value={num(rows.length)}
            context={`за всё время наблюдений`}
          />
          <MetricCard
            small
            name="Потрачено"
            value={summary.currency ? money(summary.spent, summary.currency) : DASH}
            context={
              summary.currency
                ? `${num(summary.withCost)} ${plural(summary.withCost, "размещение", "размещения", "размещений")} с указанной ценой`
                : "Валюты разные — складывать нечего."
            }
          />
          <MetricCard
            small
            name="Пришло, измерено"
            value={summary.attributed === null ? DASH : num(Math.round(summary.attributed))}
            context="Сумма по тем размещениям, где приход удалось посчитать."
          />
          <MetricCard
            small
            name="Обычная цена удержанного"
            value={
              summary.medianCpaRetained === null || !summary.currency
                ? DASH
                : money(summary.medianCpaRetained, summary.currency)
            }
            context="Медиана по вашим же закупкам — внешних «нормальных цен» не бывает."
          />
        </div>
      )}

      {rows.length === 0 ? (
        <Card>
          <Empty title="Размещений пока нет" icon={<IconMegaphone size={22} />}>
            Заведите первое: укажите канал, дату и цену. Дальше система сама посчитает приход —
            настолько точно, насколько позволяют доступные данные, и честно скажет, насколько это
            точно.
          </Empty>
        </Card>
      ) : (
        rows.map((purchase) => <PurchaseCard key={purchase.id} purchase={purchase} />)
      )}

      {methodology.data && (
        <Card title="Как считается приход" note="способ определяет, чему верить">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Способ</th>
                  <th>Точность</th>
                  <th>Что нужно</th>
                  <th>Замечание</th>
                </tr>
              </thead>
              <tbody>
                {methodology.data.methods.map((method) => (
                  <tr key={method.key}>
                    <td>{method.title}</td>
                    <td>
                      <Badge
                        tone={
                          method.accuracy === "точно"
                            ? "ok"
                            : method.accuracy === "не измерение"
                              ? "alert"
                              : "warn"
                        }
                      >
                        {method.accuracy}
                      </Badge>
                    </td>
                    <td className="dim">{method.requires}</td>
                    <td className="dim">{method.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="stack" style={{ gap: 8 }}>
            <p className="muted">
              <strong>Удержание.</strong> {methodology.data.retention}
            </p>
            <p className="muted">
              <strong>Вовлечённость пришедших.</strong> {methodology.data.engagement}
            </p>
          </div>
        </Card>
      )}
    </>
  );
}

function summarize(rows: Purchase[]) {
  const currencies = new Set(rows.filter((row) => row.cost_amount !== null).map((row) => row.currency));
  const currency = currencies.size === 1 ? [...currencies][0] : null;
  const withCost = rows.filter((row) => row.cost_amount !== null).length;
  const spent = rows.reduce((total, row) => total + (row.cost_amount ?? 0), 0);

  const attributedValues = rows
    .map((row) => row.outcomes.find((item) => item.horizon_days === 0)?.attributed)
    .filter((value): value is number => value !== null && value !== undefined);

  const cpas = rows
    .map((row) => row.outcomes.find((item) => item.horizon_days === 7)?.cpa_retained)
    .filter((value): value is number => value !== null && value !== undefined)
    .sort((left, right) => left - right);
  const middle = Math.floor(cpas.length / 2);

  return {
    currency,
    withCost,
    spent,
    attributed: attributedValues.length ? attributedValues.reduce((a, b) => a + b, 0) : null,
    medianCpaRetained: cpas.length
      ? cpas.length % 2
        ? cpas[middle]
        : (cpas[middle - 1] + cpas[middle]) / 2
      : null,
  };
}

function PurchaseCard({ purchase }: { purchase: Purchase }) {
  const { channelId, can } = useSession();
  const queryClient = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [link, setLink] = useState("");
  const [joins, setJoins] = useState("");

  const addLink = useMutation({
    mutationFn: () =>
      api.post(`/ads/${channelId}/purchases/${purchase.id}/links`, {
        link,
        name: purchase.placement_title,
        join_count: joins ? Number(joins) : null,
      }),
    onSuccess: () => {
      setLinkOpen(false);
      queryClient.invalidateQueries({ queryKey: ["purchases", channelId] });
    },
  });

  const entry = purchase.outcomes.find((item) => item.horizon_days === 0);
  const week = purchase.outcomes.find((item) => item.horizon_days === 7);
  const month = purchase.outcomes.find((item) => item.horizon_days === 30);
  const best = purchase.attribution.find((item) => item.method === entry?.method);

  return (
    <Card
      title={purchase.placement_title}
      note={
        purchase.cost_amount === null
          ? `${date(purchase.placed_at)} · цена не указана`
          : `${date(purchase.placed_at)} · ${money(purchase.cost_amount, purchase.currency)}`
      }
      actions={
        <>
          {best && <Badge tone="accent">{best.method_title}</Badge>}
          {entry && <ConfidenceBadge level={entry.confidence} />}
          {purchase.links.length > 0 && <Badge tone="ok">ссылка-приглашение</Badge>}
        </>
      }
    >
      {/* Два числа решают, повторять закупку или нет. Остальные шесть —
          контекст к ним, и поэтому они мельче. */}
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
        <MetricCard
          name="Цена удержанного"
          value={week?.cpa_retained ? money(week.cpa_retained, purchase.currency) : DASH}
          context="За того, кто остался через 7 дней. Именно за это число и стоит торговаться."
        />
        <MetricCard
          name="Осталось через 7 дней"
          value={week?.retained === null || week === undefined ? DASH : num(Math.round(week.retained))}
          context={
            entry?.attributed === null || entry === undefined
              ? "Приход посчитать не удалось."
              : `Из ${num(Math.round(entry.attributed))} пришедших.`
          }
        />
      </div>

      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "var(--s3)" }}>
        <MetricCard small name="Пришло" value={entry?.attributed === null || entry === undefined ? DASH : num(Math.round(entry.attributed))} />
        <MetricCard small name="Цена входящего" value={entry?.cpa ? money(entry.cpa, purchase.currency) : DASH} />
        <MetricCard small name="Цена тысячи показов" value={entry?.cpm ? money(entry.cpm, purchase.currency) : DASH} />
        <MetricCard
          small
          name="Отток за 7 дней"
          value={week?.churn_rate === null || week === undefined ? DASH : pct(week.churn_rate * 100, 0)}
        />
        <MetricCard
          small
          name="Отток за 30 дней"
          value={month?.churn_rate === null || month === undefined ? DASH : pct(month.churn_rate * 100, 0)}
        />
        <MetricCard
          small
          name="Читают относительно обычных"
          value={
            entry?.engagement_index === null || entry === undefined
              ? DASH
              : `${entry.engagement_index > 1 ? "+" : ""}${decimal((entry.engagement_index - 1) * 100, 0)}%`
          }
          title={entry?.engagement_basis ?? undefined}
        />
      </div>

      {purchase.recommendation && (
        <div className="action-line">
          <strong>Торгуйтесь по цене удержанного подписчика.</strong> При вашей медиане{" "}
          {money(purchase.recommendation.benchmark_cpa_retained, purchase.recommendation.currency)}{" "}
          повторная закупка разумна до{" "}
          {money(purchase.recommendation.max_cost, purchase.recommendation.currency)} за размещение.
          <div className="faint" style={{ marginTop: 6 }}>
            Ориентир посчитан по вашим собственным {purchase.recommendation.sample}{" "}
            {plural(purchase.recommendation.sample, "закупке", "закупкам", "закупкам")} — внешних
            «нормальных цен» не бывает.
          </div>
        </div>
      )}

      <Why summary="Все способы подсчёта">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Способ</th>
                <th className="num">Подписчиков</th>
                <th className="num">Интервал</th>
                <th>Уверенность</th>
                <th>Почему</th>
              </tr>
            </thead>
            <tbody>
              {purchase.attribution.map((item) => (
                <tr key={item.method}>
                  <td>{item.method_title}</td>
                  <td className="num mono">
                    {item.subscribers === null ? DASH : num(Math.round(item.subscribers))}
                  </td>
                  <td className="num mono">
                    {item.ci_low === null || item.ci_high === null
                      ? DASH
                      : `${num(Math.round(item.ci_low))}–${num(Math.round(item.ci_high))}`}
                  </td>
                  <td>
                    <ConfidenceBadge level={item.confidence} />
                  </td>
                  <td className="dim">{item.reasons.join(" ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {week?.details?.retention_note ? <p style={{ marginTop: 12 }}>{String(week.details.retention_note)}</p> : null}
        {entry?.engagement_basis ? <p style={{ marginTop: 8 }}>{entry.engagement_basis}</p> : null}
      </Why>

      {can("write_ads") && (
        <div className="row">
          <button className="small" onClick={() => setLinkOpen((value) => !value)}>
            {linkOpen ? "Отмена" : "Привязать ссылку-приглашение"}
          </button>
          {linkOpen && (
            <>
              <input
                placeholder="https://t.me/+…"
                value={link}
                onChange={(event) => setLink(event.target.value)}
                style={{ maxWidth: 260 }}
              />
              <input
                placeholder="переходов"
                inputMode="numeric"
                value={joins}
                onChange={(event) => setJoins(event.target.value)}
                style={{ maxWidth: 130 }}
              />
              <button className="primary small" onClick={() => addLink.mutate()} disabled={!link}>
                Сохранить
              </button>
            </>
          )}
        </div>
      )}
      {addLink.error && <ErrorBox error={addLink.error} />}
    </Card>
  );
}

function PurchaseForm({ channelId, onDone }: { channelId: number; onDone: () => void }) {
  const [form, setForm] = useState({
    placement_title: "",
    placed_at: new Date().toISOString().slice(0, 16),
    cost_amount: "",
    promised_views: "",
    actual_views: "",
    declared_subscribers: "",
  });

  const save = useMutation({
    mutationFn: () =>
      api.post(`/ads/${channelId}/purchases`, {
        placement_title: form.placement_title,
        placed_at: new Date(form.placed_at).toISOString(),
        cost_amount: form.cost_amount ? Number(form.cost_amount) : null,
        promised_views: form.promised_views ? Number(form.promised_views) : null,
        actual_views: form.actual_views ? Number(form.actual_views) : null,
        declared_subscribers: form.declared_subscribers ? Number(form.declared_subscribers) : null,
        currency: "RUB",
        ad_format: "post",
      }),
    onSuccess: onDone,
  });

  return (
    <Card title="Новое размещение">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid two">
          <div className="field">
            <label htmlFor="ad-where">Где разместились</label>
            <input
              id="ad-where"
              required
              value={form.placement_title}
              onChange={(event) => setForm({ ...form, placement_title: event.target.value })}
              placeholder="Название канала"
            />
          </div>
          <div className="field">
            <label htmlFor="ad-when">Когда вышло</label>
            <input
              id="ad-when"
              type="datetime-local"
              required
              value={form.placed_at}
              onChange={(event) => setForm({ ...form, placed_at: event.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="ad-cost">Цена, ₽</label>
            <input
              id="ad-cost"
              inputMode="numeric"
              value={form.cost_amount}
              onChange={(event) => setForm({ ...form, cost_amount: event.target.value })}
            />
            <span className="hint">Пусто — не ноль: денежные показатели просто не посчитаются.</span>
          </div>
          <div className="field">
            <label htmlFor="ad-promised">Обещанный охват</label>
            <input
              id="ad-promised"
              inputMode="numeric"
              value={form.promised_views}
              onChange={(event) => setForm({ ...form, promised_views: event.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="ad-actual">Фактический охват</label>
            <input
              id="ad-actual"
              inputMode="numeric"
              value={form.actual_views}
              onChange={(event) => setForm({ ...form, actual_views: event.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="ad-declared">Приход со слов продавца</label>
            <input
              id="ad-declared"
              inputMode="numeric"
              value={form.declared_subscribers}
              onChange={(event) => setForm({ ...form, declared_subscribers: event.target.value })}
            />
            <span className="hint">Хранится отдельно и с измеренным не смешивается.</span>
          </div>
        </div>
        {save.error && <ErrorBox error={save.error} />}
        <button className="primary" type="submit" disabled={save.isPending} style={{ marginTop: "var(--s3)" }}>
          Сохранить и посчитать
        </button>
      </form>
    </Card>
  );
}
